// ============================================================================
// OBT F3 — Müdür "Park Ekle" diyaloğu (aday kümesi dışından park)
// ============================================================================
// Müdür, bütçe/yönetim kararıyla nihai listeye SONRADAN park ekler. Aday kümesi
// (MY onaylı cevaplar) DIŞINDAN bir park seçilebilir; bu park için Formen cevabı
// veya BM incelemesi BULUNMAYABİLİR — sahte değer gösterilmez. Gerekçe ZORUNLU.
// Arama SINIRLIDIR (backend limit tavanı) — kontrolsüz dev liste indirilmez.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, MapPin, Search, Check } from 'lucide-react';
import { listDirectorParkCandidates } from './reviewApi';
import { normalizeReviewError } from './reviewErrors';
import { MAX_COMMENT_LEN, isRequiredCommentValid } from './reviewValidation';

const SCOPE_STATE_LABELS = Object.freeze({
  in_publication: 'Yayın kapsamında',
  supplemental: 'Sonradan eklenen kapsam',
  outside: 'Yayın kapsamı dışında',
});

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {string|null} props.decisionSetId   aktif TASLAK set kimliği (draftId)
 * @param {(parkId: string, reason: string) => Promise<void>} props.onAdd
 * @param {() => void} props.onClose
 * @param {string|null} [props.error]         sunucu ekleme hatası (409/422/403)
 */
export default function DirectorAddParkDialog({
  open,
  decisionSetId,
  onAdd,
  onClose,
  error = null,
}) {
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const abortRef = useRef(null);

  // Diyalog her açılışta temiz başlar.
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(null);
      setReason('');
      setSubmitting(false);
      setSearchError(null);
    }
  }, [open]);

  // Arama (debounce) — aktif taslak yoksa çağrılmaz.
  useEffect(() => {
    if (!open || !decisionSetId) return undefined;
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setSearching(true);
    setSearchError(null);
    const handle = setTimeout(async () => {
      try {
        const data = await listDirectorParkCandidates(
          decisionSetId,
          { search: query.trim() || undefined, limit: 20 },
          { signal: controller.signal }
        );
        if (controller.signal.aborted) return;
        setCandidates(Array.isArray(data?.items) ? data.items : []);
        setSearching(false);
      } catch (e) {
        if (
          controller.signal.aborted ||
          e?.code === 'ERR_CANCELED' ||
          e?.name === 'CanceledError'
        ) {
          return;
        }
        setSearchError(normalizeReviewError(e));
        setCandidates([]);
        setSearching(false);
      }
    }, 250);
    return () => {
      clearTimeout(handle);
      controller.abort();
    };
  }, [open, decisionSetId, query]);

  const valid = Boolean(selected) && isRequiredCommentValid(reason);

  const handleConfirm = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await onAdd(selected.park_id, reason.trim());
      onClose();
    } catch {
      // Hata `error` prop'u ile gösterilir; diyalog açık kalır.
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nihai Listeye Park Ekle</DialogTitle>
          <DialogDescription>
            Bu park <span className="font-semibold">Müdür kararıyla</span> nihai
            listeye eklenecektir. Park için Formen cevabı veya BM incelemesi
            bulunmayabilir.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="director-park-search">Park ara</Label>
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400"
                aria-hidden="true"
              />
              <Input
                id="director-park-search"
                value={query}
                placeholder="Park adına göre ara…"
                className="pl-8"
                onChange={(e) => setQuery(e.target.value)}
                disabled={submitting}
              />
            </div>
          </div>

          <div
            className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-1"
            role="listbox"
            aria-label="Park adayları"
          >
            {searching && (
              <p className="flex items-center gap-2 p-3 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                Parklar aranıyor…
              </p>
            )}
            {!searching && searchError && (
              <p role="alert" className="p-3 text-sm text-red-600">
                {searchError.message}
              </p>
            )}
            {!searching && !searchError && candidates.length === 0 && (
              <p className="p-3 text-sm text-gray-500">
                Eşleşen park bulunamadı.
              </p>
            )}
            {!searching &&
              !searchError &&
              candidates.map((c) => {
                const disabled = c.already_in_final_set || submitting;
                const isSelected = selected?.park_id === c.park_id;
                return (
                  <button
                    key={String(c.park_id)}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={disabled}
                    onClick={() => setSelected(c)}
                    className={`flex w-full items-center justify-between gap-2 rounded px-2.5 py-2 text-left text-sm ${
                      isSelected
                        ? 'bg-blue-50 ring-1 ring-inset ring-blue-300'
                        : 'hover:bg-gray-50'
                    } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                  >
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 font-medium text-gray-900">
                        <MapPin className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
                        {c.park_name}
                      </span>
                      <span className="block pl-5 text-xs text-gray-500">
                        {c.region_name || 'Bölge bilinmiyor'}
                        {' · '}
                        {SCOPE_STATE_LABELS[c.current_scope_state] || c.current_scope_state}
                        {c.already_in_final_set ? ' · Zaten listede' : ''}
                      </span>
                    </span>
                    {isSelected && (
                      <Check className="h-4 w-4 text-blue-600" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="director-park-reason">Ekleme gerekçesi *</Label>
            <Textarea
              id="director-park-reason"
              value={reason}
              maxLength={MAX_COMMENT_LEN}
              rows={3}
              placeholder="Bu parkın neden eklendiğini yazın (zorunlu)…"
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Vazgeç
          </Button>
          <Button onClick={handleConfirm} disabled={!valid || submitting}>
            {submitting && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            Parkı Ekle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
